/**
 * Informational card for the signed-in parent: avatar, name, and contact details.
 */
import { useTranslation } from "react-i18next";
import { Platform, View } from "react-native";

import { TextWithToolTip } from "@/components/TextWithToolTip";
import { UserAvatar } from "@/components/UserAvatar";
import { Text } from "@/components/ui/text";
import { useAuth } from "@/lib/auth";
import { useGetProfilesQuery } from "@/query/apiSlice";
import { TEST_IDS } from "@/testIdDefinition";

import { resolveParentCardInfo } from "./parentUserCardInfo";

export function ParentUserCard({ size = "xs" }: { size?: "sm" | "md" | "xs" }) {
    const { t } = useTranslation();
    const { authContext } = useAuth();
    const { data: profiles } = useGetProfilesQuery();
    const info = resolveParentCardInfo(authContext, profiles);

    if (!info) {
        return null;
    }

    const displayName = info.name || t("profile.placeholder");

    return (
        <View testID={TEST_IDS[TEST_IDS.MENU_PARENT_CARD]} className="p-4">
            <View className={"flex-row items-center gap-3"}>
                <UserAvatar
                    firstName={info.firstName}
                    lastName={info.lastName}
                    name={displayName}
                    size={size}
                />
                <View className="min-w-0 flex-1">
                    <Text
                        variant="bold"
                        numberOfLines={1}
                        className={
                            Platform.OS === "web" ? "text-sm" : "text-base"
                        }
                        testID={TEST_IDS[TEST_IDS.MENU_PARENT_CARD_NAME]}
                    >
                        {displayName}
                    </Text>
                    {info.email ? (
                        <TextWithToolTip
                            variant="muted"
                            className={
                                Platform.OS === "web" ? "text-xs" : "text-sm"
                            }
                            numberOfLines={1}
                            testID={TEST_IDS[TEST_IDS.MENU_PARENT_CARD_EMAIL]}
                        >
                            {info.email}
                        </TextWithToolTip>
                    ) : null}
                </View>
            </View>
        </View>
    );
}
